'use client';

import { useReducer, useCallback, useEffect, useMemo, useRef } from 'react';
import { qurvo, datadogView } from '../bootstrap/analyticsQueue';
import type { NavigationState, NavigationAction, ScreenName, ScreenParams, TabStack } from '../types/navigation';

const initialState: NavigationState = {
  activeTab: 'balance',
  stacks: {
    balance: [{ screen: 'balance' }],
    cases: [{ screen: 'cases' }],
    games: [{ screen: 'games' }],
    inventory: [{ screen: 'inventory' }],
    referrals: [{ screen: 'referrals' }],
    profile: [{ screen: 'profile' }],
    onboarding: [{ screen: 'onboarding' }],
    shop: [{ screen: 'shop' }],
  },
};

function navigationReducer(state: NavigationState, action: NavigationAction): NavigationState {
  switch (action.type) {
    case 'CHANGE_TAB':
      return {
        ...state,
        activeTab: action.tab,
      };

    case 'PUSH_SCREEN': {
      const newStack = [...state.stacks[action.tab], { screen: action.screen, params: action.params }];
      return {
        ...state,
        stacks: {
          ...state.stacks,
          [action.tab]: newStack,
        },
      };
    }

    case 'REPLACE_SCREEN': {
      const currentStack = state.stacks[action.tab];
      if (currentStack.length === 0) {
        return state;
      }
      const newStack = [...currentStack];
      newStack[newStack.length - 1] = { screen: action.screen, params: action.params };
      return {
        ...state,
        stacks: {
          ...state.stacks,
          [action.tab]: newStack,
        },
      };
    }

    case 'POP_SCREEN': {
      const currentStack = state.stacks[action.tab];
      if (currentStack.length <= 1) {
        return state; // Can't pop the root screen
      }

      const newStack = currentStack.slice(0, -1);

      return {
        ...state,
        stacks: {
          ...state.stacks,
          [action.tab]: newStack,
        },
      };
    }

    case 'RESET_STACK': {
      return {
        ...state,
        stacks: {
          ...state.stacks,
          [action.tab]: [{ screen: action.screen, params: action.params }],
        },
      };
    }

    case 'UPDATE_SCREEN_PARAMS': {
      const currentStack = state.stacks[action.tab];
      if (currentStack.length === 0) {
        return state;
      }

      const newStack = [...currentStack];
      const lastIndex = newStack.length - 1;
      newStack[lastIndex] = {
        ...newStack[lastIndex],
        params: {
          ...newStack[lastIndex].params,
          ...action.params,
        },
      };

      return {
        ...state,
        stacks: {
          ...state.stacks,
          [action.tab]: newStack,
        },
      };
    }

    default:
      return state;
  }
}

export function useNavigation() {
  const [state, dispatch] = useReducer(navigationReducer, initialState);

  // Track view changes in analytics. Both SDKs go through the analyticsQueue
  // façade so app code never pulls @datadog/browser-rum or qurvo onto the eager
  // critical path (they load lazily in bootstrap/analytics.ts).
  //
  // The very first screen is left to Datadog's auto-instrumented initial view
  // (URL `/`) — that view carries the initial-load LCP we care about most, so we
  // don't cut it short with a manual startView. Every navigation after gets a
  // named RUM view; qurvo still records the initial screen too.
  const isInitialView = useRef(true);
  useEffect(() => {
    const currentStack = state.stacks[state.activeTab];
    const currentScreen = currentStack[currentStack.length - 1];
    const viewName = `${state.activeTab}/${currentScreen.screen}`;
    qurvo.screen(viewName);
    if (isInitialView.current) {
      isInitialView.current = false;
    } else {
      datadogView.start(viewName);
    }
  }, [state.activeTab, state.stacks]);

  const changeTab = useCallback((tab: keyof TabStack) => {
    dispatch({ type: 'CHANGE_TAB', tab });
  }, []);

  const pushScreen = useCallback((tab: keyof TabStack, screen: ScreenName, params?: ScreenParams) => {
    dispatch({ type: 'PUSH_SCREEN', tab, screen, params });
  }, []);

  const replaceScreen = useCallback((tab: keyof TabStack, screen: ScreenName, params?: ScreenParams) => {
    dispatch({ type: 'REPLACE_SCREEN', tab, screen, params });
  }, []);

  const popScreen = useCallback((tab: keyof TabStack) => {
    dispatch({ type: 'POP_SCREEN', tab });
  }, []);

  const resetStack = useCallback((tab: keyof TabStack, screen: ScreenName, params?: ScreenParams) => {
    dispatch({ type: 'RESET_STACK', tab, screen, params });
  }, []);

  const goBack = useCallback(() => {
    popScreen(state.activeTab);
  }, [state.activeTab, popScreen]);

  const navigate = useCallback(
    (screen: ScreenName, params?: ScreenParams) => {
      pushScreen(state.activeTab, screen, params);
    },
    [state.activeTab, pushScreen],
  );

  const getCurrentScreen = useCallback(() => {
    const currentStack = state.stacks[state.activeTab];
    return currentStack[currentStack.length - 1];
  }, [state.stacks, state.activeTab]);

  const canGoBack = useCallback(() => {
    return state.stacks[state.activeTab].length > 1;
  }, [state.stacks, state.activeTab]);

  const updateCurrentParams = useCallback(
    (params: ScreenParams) => {
      dispatch({ type: 'UPDATE_SCREEN_PARAMS', tab: state.activeTab, params });
    },
    [state.activeTab],
  );

  return useMemo(
    () => ({
      state,
      changeTab,
      pushScreen,
      replaceScreen,
      popScreen,
      resetStack,
      goBack,
      navigate,
      getCurrentScreen,
      canGoBack,
      updateCurrentParams,
    }),
    [
      state,
      changeTab,
      pushScreen,
      replaceScreen,
      popScreen,
      resetStack,
      goBack,
      navigate,
      getCurrentScreen,
      canGoBack,
      updateCurrentParams,
    ],
  );
}
